import React, { ReactNode, useEffect, useState } from 'react';
import Link, { LinkProps } from 'next/link';
import { useForcedSearchParams } from '../../contexts/useForcedSearchParams';

// When we only reload a single section, the links in other sections may become outdated.
// This array holds the query param keys that links should mirror when they change in the URL.
// For example, the throughput sections reload themselves but change the "throughput" query param,
// so it needs to be specified here so that other links (in sections that are not being reloaded) can keep it in sync.
const dynamicQueryParams = ['throughput'];

function LinkWrapper({
  href: initialHref,
  dynamicQueryParamsToIgnore,
  children,
  ...rest
}: {
  href: string;
  className?: string;
  // If we don't want this link to keep a query param in sync, specify it here.
  // For example, links to different throughput sections will want to keep their throughput values.
  dynamicQueryParamsToIgnore?: string[];
  children: ReactNode;
} & Partial<LinkProps>): JSX.Element {
  const [href, setHref] = useState(initialHref);
  useEffect(() => {
    setHref(initialHref);
  }, [initialHref]);

  const { forcedSearchParams } = useForcedSearchParams();
  useEffect(() => {
    setHref((prev) => {
      if (prev.startsWith('#')) {
        return prev;
      }

      const forcedKeys = Object.keys(forcedSearchParams);
      const searchParams = new URLSearchParams(
        (prev.split('?')[1] ?? '').split('#')[0]
      );

      dynamicQueryParams.forEach((key) => {
        if (
          !dynamicQueryParamsToIgnore?.includes(key) &&
          forcedKeys.includes(key)
        ) {
          const forcedValue = forcedSearchParams[key];
          if (forcedValue !== null) {
            searchParams.set(key, forcedValue);
          } else {
            searchParams.delete(key);
          }
        }
      });

      const hash = prev.split('#')[1];

      return `${prev.split('?')[0]}?${searchParams.toString()}${
        hash ? `#${hash}` : ''
      }`;
    });
  }, [forcedSearchParams, dynamicQueryParamsToIgnore]);

  return (
    <Link {...rest} href={href}>
      {children}
    </Link>
  );
}

export default LinkWrapper;
